import { Link, Outlet } from "@tanstack/react-router";
import { Settings } from "lucide-react";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import { StatusPill } from "../design-system/StatusPill";
import { ShellTopBar } from "./shell/ShellTopBar";
import { primaryNavigation, settingsNavigation, type NavigationItem } from "./shell/navigation";
import { TrackingRuntime } from "../features/tracking/TrackingRuntime";
import { SystemBannerRegion } from "./SystemBannerRegion";
import { useOnlineStatus } from "./useOnlineStatus";

interface NavigationLinkProps {
  compact?: boolean;
  item: NavigationItem;
}

function NavigationLink({ compact = false, item }: NavigationLinkProps) {
  const { t } = useTranslation("foundation");
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      className={compact ? "navigation-link navigation-link--compact" : "navigation-link"}
      activeProps={{ className: "navigation-link navigation-link--active" }}
      activeOptions={{ exact: item.to === "/" }}
    >
      <Icon aria-hidden="true" size={20} strokeWidth={1.8} />
      <span>{t(item.label)}</span>
    </Link>
  );
}

function focusMainContent(event: MouseEvent<HTMLAnchorElement>) {
  event.preventDefault();
  const mainContent = document.querySelector<HTMLElement>("#main-content");
  window.requestAnimationFrame(() => {
    mainContent?.focus();
  });
}

function AppShellContent() {
  const { t } = useTranslation("foundation");
  const isOnline = useOnlineStatus();

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content" onClick={focusMainContent}>
        {t("app.skipToContent")}
      </a>

      <aside className="sidebar" aria-label={t("shell.sidebarLabel")}>
        <div className="brand-lockup">
          <span className="brand-mark" aria-hidden="true">
            T
          </span>
          <div>
            <strong>{t("app.name")}</strong>
            <span>{t("shell.environment")}</span>
          </div>
        </div>

        <nav className="sidebar-navigation" aria-label={t("navigation.desktopLabel")}>
          {primaryNavigation.map((item) => (
            <NavigationLink key={item.to} item={item} />
          ))}
        </nav>

        {/* Connection state lives in the toolbar at this width, not twice. */}
        <div className="sidebar-footer">
          <NavigationLink item={settingsNavigation} />
        </div>
      </aside>

      <div className="main-column">
        <header className="mobile-header">
          <div className="mobile-brand">
            <span className="brand-mark" aria-hidden="true">
              T
            </span>
            <strong>{t("app.name")}</strong>
          </div>
          <div className="mobile-header-actions">
            <StatusPill tone={isOnline ? "success" : "warning"}>
              <span className="status-dot" aria-hidden="true" />
              {isOnline ? t("shell.online") : t("shell.offline")}
            </StatusPill>
            <Link
              to={settingsNavigation.to}
              className="mobile-settings-link"
              activeProps={{
                className: "mobile-settings-link mobile-settings-link--active",
              }}
              aria-label={t(settingsNavigation.label)}
            >
              <Settings aria-hidden="true" size={20} strokeWidth={1.8} />
            </Link>
          </div>
        </header>

        {/*
          One toolbar for every width above compact. Rendering a second search
          field for tablet would publish two `search` landmarks, so the same
          one is restyled per breakpoint instead.
        */}
        <ShellTopBar />

        <SystemBannerRegion isOnline={isOnline} />

        <main id="main-content" className="page-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <nav className="bottom-navigation" aria-label={t("navigation.mobileLabel")}>
        {primaryNavigation.map((item) => (
          <NavigationLink key={item.to} item={item} compact />
        ))}
      </nav>
    </div>
  );
}

export function AppShell() {
  return (
    <TrackingRuntime>
      <AppShellContent />
    </TrackingRuntime>
  );
}
